import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    FormLabel,
    HStack,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Switch,
    Text,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import PageCard from "../../../components/page-card";
import { translate } from "../../../i18n";
import { useSelectedOrganization } from "../hooks/useSelectedOrganization";
import { useUpdateOrganizationPayForOthersEnabled } from "../hooks/useUpdateOrganizationPayForOthersEnabled";

// Mirrors api/src/organization/pay-for-others-limits.ts. The API re-asserts
// these; the inputs here only keep an operator from typing a value the save
// would refuse.
export const PAY_FOR_OTHERS_DAILY_CAP_MIN = 1;
export const PAY_FOR_OTHERS_DAILY_CAP_MAX = 50;
export const PAY_FOR_OTHERS_DAILY_CAP_DEFAULT = 5;
// Mirrors GUESTS_PER_GAME_* in api/src/organization/pay-for-others-limits.ts.
export const GUESTS_PER_GAME_MIN = 1;
export const GUESTS_PER_GAME_MAX = 10;
export const GUESTS_PER_GAME_DEFAULT = 2;

// Per-tenant feature gate for the mobile "Pay for others" flow, plus its
// anti-abuse ceiling. Operators flip the switch from the dashboard; the value
// is read by the mobile client to decide whether to show the multi-player
// picker, and is enforced server-side on the batch payment endpoint (403
// `PAY_FOR_OTHERS_DISABLED` when a roster covers someone else).
//
// The switch is the source of truth — no Save button, the mutation fires on
// each flip. The daily cap is a number input WITH a Save button, because a
// stepper that fires a mutation on every tick would write five times to get
// from 5 to 10.
export default function PayForOthersSettingsCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const textColorPrimary = useColorModeValue('secondaryGray.900', 'white');
    const toast = useToast();

    // Default to false so old API responses without the field don't render
    // the switch in an undefined-checked state. The API treats absence as
    // false too, so this stays consistent across the wire.
    const enabled = selectedOrganization?.payForOthersEnabled ?? false;
    const savedCap =
        selectedOrganization?.payForOthersDailyCap ?? PAY_FOR_OTHERS_DAILY_CAP_DEFAULT;
    const guestsEnabled = selectedOrganization?.guestPlayersEnabled ?? false;
    const consentRequired = selectedOrganization?.payForOthersConsentRequired ?? false;
    const savedGuestsMax =
        selectedOrganization?.guestsPerGameMax ?? GUESTS_PER_GAME_DEFAULT;

    // Local draft of the cap, as a string so a half-typed value ("") does not
    // snap back to a number under the operator's cursor.
    const [capDraft, setCapDraft] = useState<string>(String(savedCap));
    useEffect(() => {
        setCapDraft(String(savedCap));
    }, [savedCap, selectedOrganization?.id]);
    const [guestsMaxDraft, setGuestsMaxDraft] = useState<string>(String(savedGuestsMax));
    useEffect(() => {
        setGuestsMaxDraft(String(savedGuestsMax));
    }, [savedGuestsMax, selectedOrganization?.id]);

    const { mutate, isLoading } = useUpdateOrganizationPayForOthersEnabled(
        selectedOrganization?.id || '',
        () => {
            toast({
                title: translate('organization.pay_for_others_saved'),
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        },
    );

    const onError = () => {
        toast({
            title: translate('error'),
            status: 'error',
            duration: 3000,
            position: 'top-right',
        });
    };

    const handleToggle = (nextEnabled: boolean) => {
        if (!selectedOrganization) return;
        if (isLoading) return; // guard against double-flips while in flight
        mutate({ payForOthersEnabled: nextEnabled }, { onError });
    };

    const parsedCap = Number(capDraft);
    const capIsValid =
        capDraft.trim() !== '' &&
        Number.isInteger(parsedCap) &&
        parsedCap >= PAY_FOR_OTHERS_DAILY_CAP_MIN &&
        parsedCap <= PAY_FOR_OTHERS_DAILY_CAP_MAX;
    const capIsDirty = capIsValid && parsedCap !== savedCap;

    const handleSaveCap = () => {
        if (!selectedOrganization) return;
        if (isLoading || !capIsDirty) return;
        mutate(
            { payForOthersDailyCap: parsedCap },
            { onError },
        );
    };

    // Guest players: the switch saves on flip (like the main one); the
    // per-game ceiling has its own Save, like the daily cap.
    const handleToggleGuests = (next: boolean) => {
        if (!selectedOrganization) return;
        if (isLoading) return;
        mutate({ guestPlayersEnabled: next }, { onError });
    };
    // Held seats: saves on flip, like the other switches.
    const handleToggleConsent = (next: boolean) => {
        if (!selectedOrganization) return;
        if (isLoading) return;
        mutate({ payForOthersConsentRequired: next }, { onError });
    };
    const parsedGuestsMax = Number(guestsMaxDraft);
    const guestsMaxIsValid =
        guestsMaxDraft.trim() !== '' &&
        Number.isInteger(parsedGuestsMax) &&
        parsedGuestsMax >= GUESTS_PER_GAME_MIN &&
        parsedGuestsMax <= GUESTS_PER_GAME_MAX;
    const guestsMaxIsDirty = guestsMaxIsValid && parsedGuestsMax !== savedGuestsMax;
    const handleSaveGuestsMax = () => {
        if (!selectedOrganization) return;
        if (isLoading || !guestsMaxIsDirty) return;
        mutate(
            { guestsPerGameMax: parsedGuestsMax },
            { onError },
        );
    };

    if (!selectedOrganization) return null;

    return (
        <PageCard title={translate('organization.pay_for_others_title')}>
            <Box p={4}>
                <Box display="flex" alignItems="center" gap={3} mb={2}>
                    <Switch
                        isChecked={enabled}
                        isDisabled={isLoading}
                        onChange={(e) => handleToggle(e.target.checked)}
                        colorScheme="brand"
                        aria-label={translate('organization.pay_for_others_title')}
                    />
                    <FormLabel mb={0} color={textColorPrimary}>
                        {translate('organization.pay_for_others_title')}
                    </FormLabel>
                </Box>
                <Text fontSize="sm" color="gray.500">
                    {translate('organization.pay_for_others_helper')}
                </Text>

                <FormControl mt={5} isInvalid={!capIsValid}>
                    <FormLabel color={textColorPrimary} fontSize="sm">
                        {translate('organization.pay_for_others_daily_cap_label')}
                    </FormLabel>
                    <HStack align="flex-start">
                        <NumberInput
                            value={capDraft}
                            min={PAY_FOR_OTHERS_DAILY_CAP_MIN}
                            max={PAY_FOR_OTHERS_DAILY_CAP_MAX}
                            step={1}
                            precision={0}
                            maxW="120px"
                            isDisabled={isLoading}
                            onChange={(valueAsString) => setCapDraft(valueAsString)}
                        >
                            <NumberInputField
                                aria-label={translate('organization.pay_for_others_daily_cap_label')}
                            />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                        <Button
                            size="sm"
                            colorScheme="brand"
                            onClick={handleSaveCap}
                            isDisabled={!capIsDirty || isLoading}
                            isLoading={isLoading}
                        >
                            {translate('common.save')}
                        </Button>
                    </HStack>
                    <FormHelperText color={capIsValid ? 'gray.500' : 'red.400'}>
                        {capIsValid
                            ? translate('organization.pay_for_others_daily_cap_helper')
                            : translate('organization.pay_for_others_daily_cap_invalid')}
                    </FormHelperText>
                </FormControl>

                <Box display="flex" alignItems="center" gap={3} mt={6} mb={2}>
                    <Switch
                        isChecked={guestsEnabled}
                        isDisabled={isLoading}
                        onChange={(e) => handleToggleGuests(e.target.checked)}
                        colorScheme="brand"
                        aria-label={translate('organization.guest_players_label')}
                    />
                    <FormLabel mb={0} color={textColorPrimary}>
                        {translate('organization.guest_players_label')}
                    </FormLabel>
                </Box>
                <Text fontSize="sm" color="gray.500">
                    {translate('organization.guest_players_helper')}
                </Text>

                <FormControl mt={4} isInvalid={!guestsMaxIsValid}>
                    <FormLabel color={textColorPrimary} fontSize="sm">
                        {translate('organization.guests_per_game_label')}
                    </FormLabel>
                    <HStack align="flex-start">
                        <NumberInput
                            value={guestsMaxDraft}
                            min={GUESTS_PER_GAME_MIN}
                            max={GUESTS_PER_GAME_MAX}
                            step={1}
                            precision={0}
                            maxW="120px"
                            isDisabled={isLoading}
                            onChange={(valueAsString) => setGuestsMaxDraft(valueAsString)}
                        >
                            <NumberInputField
                                aria-label={translate('organization.guests_per_game_label')}
                            />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                        <Button
                            size="sm"
                            colorScheme="brand"
                            onClick={handleSaveGuestsMax}
                            isDisabled={!guestsMaxIsDirty || isLoading}
                            isLoading={isLoading}
                        >
                            {translate('common.save')}
                        </Button>
                    </HStack>
                    <FormHelperText color={guestsMaxIsValid ? 'gray.500' : 'red.400'}>
                        {guestsMaxIsValid
                            ? translate('organization.guests_per_game_helper')
                            : translate('organization.guests_per_game_invalid')}
                    </FormHelperText>
                </FormControl>

                <Box display="flex" alignItems="center" gap={3} mt={6} mb={2}>
                    <Switch
                        isChecked={consentRequired}
                        isDisabled={isLoading}
                        onChange={(e) => handleToggleConsent(e.target.checked)}
                        colorScheme="brand"
                        aria-label={translate('organization.pay_for_others_consent_label')}
                    />
                    <FormLabel mb={0} color={textColorPrimary}>
                        {translate('organization.pay_for_others_consent_label')}
                    </FormLabel>
                </Box>
                <Text fontSize="sm" color="gray.500">
                    {translate('organization.pay_for_others_consent_helper')}
                </Text>
            </Box>
        </PageCard>
    );
}
